'use client';

/**
 * Style Guidance Panel
 *
 * @component_role Persistent Style Board (DesignIntent) manager
 * @business_rule Lets users save a moodboard — name + brief + 1-3 reference
 *   images — as a reusable "style guide" (DI_xxx). Saved guides are enriched
 *   server-side into a vibe vector that drives zero-cost semantic search.
 *
 *   Labels come in as a prop from StyleGuidanceSection, which gets them
 *   from the SchemaExplorer, which gets them from the total-matrix store's
 *   `explorer_labels.discovery` dict. All user-facing strings reference
 *   `L.key_name` — no hardcoded English, no fallback, no locale lookup on
 *   the client. The server decides the language via `/ui/total-matrix?lang=zh`.
 *
 * @user_outcome Users can crystallise an aesthetic direction once and recall
 *   it across sessions instead of re-uploading the same images every time.
 * @integration_point klyde_edge BFF — style_create, style_list, style_delete
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain discovery
 */

import { useCallback, useRef, useState } from 'react';
import { Image as ImageIcon, Loader2, Palette, Plus, Save, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils/cn';
import { isValidImageFile, processImageForObserve } from '@/lib/utils/image-processing';
import { useStrategyData, useStyleCreate, useStyleDelete, useObeyaProjects } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import type { LabelsDict, StyleGuidanceItem, StyleListResponse } from '@/lib/api/types';

// ============================================================
// Config
// ============================================================
const STYLE_GUIDANCE_CONFIG = {
  MAX_IMAGES: 3,          // Experiment limit — backend accepts up to 5
  IMAGE_SLOT_SIZE: 14,    // Tailwind size unit (w-14 h-14)
};

// ============================================================
// Types
// ============================================================
interface StagedImage {
  id: string;
  base64: string;
  thumbnail: string;
  filename: string;
  sizeKB: number;
}

// ============================================================
// Helpers
// ============================================================
function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Format a relative date using server-provided label templates.
 * The templates use `{n}` as the count placeholder.
 */
function formatRelativeDate(iso: string, L: LabelsDict): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 0) return L.time_today ?? '';
  if (days === 1) return L.time_1_day_ago ?? '';
  if (days < 7) return (L.time_days_ago ?? '').replace('{n}', String(days));
  if (days < 30) return (L.time_weeks_ago ?? '').replace('{n}', String(Math.floor(days / 7)));
  return (L.time_months_ago ?? '').replace('{n}', String(Math.floor(days / 30)));
}

// ============================================================
// Image Slot — fills its grid cell so slots shrink with the pane
// ============================================================
function ImageSlot({
  image,
  onAdd,
  onRemove,
  disabled,
}: {
  image?: StagedImage;
  onAdd: () => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  if (image) {
    return (
      <div className="relative group w-full aspect-square">
        <img
          src={image.thumbnail}
          alt={image.filename}
          className="w-full h-full object-cover rounded border border-border"
        />
        <button
          type="button"
          onClick={onRemove}
          className={cn(
            'absolute -top-1 -right-1 w-4 h-4 rounded-full',
            'bg-destructive text-destructive-foreground',
            'flex items-center justify-center',
            'opacity-0 group-hover:opacity-100 transition-opacity',
            'hover:bg-destructive/90',
          )}
        >
          <X className="w-2.5 h-2.5" />
        </button>
        <span className="absolute bottom-0 right-0 text-[8px] bg-background/80 px-0.5 rounded-tl">
          {image.sizeKB}KB
        </span>
      </div>
    );
  }

  if (disabled) return <div className="w-full aspect-square" />;

  return (
    <button
      type="button"
      onClick={onAdd}
      className={cn(
        'w-full aspect-square border-2 border-dashed border-muted-foreground/30 rounded',
        'flex items-center justify-center',
        'hover:border-primary hover:bg-accent/50 transition-colors',
      )}
    >
      <Plus className="w-4 h-4 text-muted-foreground" />
    </button>
  );
}

// ============================================================
// Style Guide Card — one row in the saved guides list
// ============================================================
function StyleGuideCard({
  item,
  onDelete,
  isDeleting,
  labels: L,
}: {
  item: StyleGuidanceItem;
  onDelete: (intent_sid: string) => void;
  isDeleting: boolean;
  labels: LabelsDict;
}) {
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const selectedId = useGraphSelectionStore((s) => s.selectedNode?.id ?? null);
  const isSelected = selectedId === item.semantic_id;

  const handleSelect = useCallback(() => {
    selectNode({
      id: item.semantic_id,
      label: item.name,
      type: 'design_intent',
    });
  }, [item.semantic_id, item.name, selectNode]);

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      // Prevent the row click handler from firing when the delete button is clicked
      e.stopPropagation();
      const confirmMsg = (L.style_delete_confirm ?? '').replace('{name}', item.name);
      if (typeof window !== 'undefined' && !window.confirm(confirmMsg)) return;
      onDelete(item.semantic_id);
    },
    [item.name, item.semantic_id, onDelete, L.style_delete_confirm],
  );

  const imageCountLabel =
    item.image_count === 1 ? L.style_image_count_singular : L.style_image_count_plural;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      }}
      className={cn(
        'group flex items-start gap-2 px-3 py-2 rounded transition-colors cursor-pointer',
        isSelected ? 'bg-primary/10 border border-primary/30' : 'hover:bg-accent/50',
      )}
    >
      <Palette className="w-4 h-4 mt-0.5 shrink-0 text-primary/70" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{item.name}</p>
        {item.brief && <p className="text-xs text-muted-foreground truncate">{item.brief}</p>}
        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
          <span>
            {item.image_count} {imageCountLabel}
          </span>
          <span>·</span>
          <span>{formatRelativeDate(item.created_at, L)}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={handleDelete}
        disabled={isDeleting}
        className={cn(
          'p-1 rounded text-muted-foreground',
          'opacity-0 group-hover:opacity-100 transition-opacity',
          'hover:bg-destructive/10 hover:text-destructive',
          'disabled:opacity-30 disabled:cursor-not-allowed',
        )}
        title={L.style_delete_tooltip}
      >
        {isDeleting ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Trash2 className="w-3.5 h-3.5" />
        )}
      </button>
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================
export function StyleGuidance({ labels: L }: { labels: LabelsDict }) {
  const [name, setName] = useState('');
  const [brief, setBrief] = useState('');
  const [projectSid, setProjectSid] = useState<string | null>(null);
  const [stagedImages, setStagedImages] = useState<StagedImage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [deletingSid, setDeletingSid] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { MAX_IMAGES } = STYLE_GUIDANCE_CONFIG;

  // Capability resolution — standard pattern across all explorers.
  const createStrategy = useCapabilityStore((s) => s.strategyName('discovery', 'create_style'));
  const listStrategy = useCapabilityStore((s) => s.strategyName('discovery', 'list_styles'));
  const deleteStrategy = useCapabilityStore((s) => s.strategyName('discovery', 'delete_style'));
  const projectListStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));

  // Project list for the attachment picker
  const { data: projectsData } = useObeyaProjects(projectListStrategy!, { limit: 50 });

  const listQuery = useStrategyData<StyleListResponse>(
    listStrategy,
    {},
    !!listStrategy,
    60_000, // refresh every minute — saves are rare, no need to hammer
  );
  const createMutation = useStyleCreate(createStrategy!);
  const deleteMutation = useStyleDelete(deleteStrategy!);

  const items = listQuery.data?.data?.intents ?? [];

  // --------------------------------------------------------
  // Image management
  // --------------------------------------------------------
  const addImages = useCallback(
    async (files: File[]) => {
      const available = MAX_IMAGES - stagedImages.length;
      if (available <= 0) {
        toast.warning((L.style_max_images ?? '').replace('{max}', String(MAX_IMAGES)));
        return;
      }

      const validFiles = files.filter(isValidImageFile).slice(0, available);
      if (validFiles.length === 0) {
        toast.warning(L.style_no_valid_images);
        return;
      }

      setIsProcessing(true);
      for (const file of validFiles) {
        try {
          const { base64, sizeKB } = await processImageForObserve(file);
          setStagedImages((prev) => [
            ...prev,
            {
              id: generateId(),
              base64,
              thumbnail: URL.createObjectURL(file),
              filename: file.name,
              sizeKB,
            },
          ]);
        } catch (err) {
          console.error('[style-guidance] Failed to process image:', file.name, err);
        }
      }
      setIsProcessing(false);
    },
    [stagedImages.length, MAX_IMAGES, L.style_max_images, L.style_no_valid_images],
  );

  const removeImage = useCallback((id: string) => {
    setStagedImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img) URL.revokeObjectURL(img.thumbnail);
      return prev.filter((i) => i.id !== id);
    });
  }, []);

  const clearForm = useCallback(() => {
    stagedImages.forEach((img) => URL.revokeObjectURL(img.thumbnail));
    setStagedImages([]);
    setName('');
    setBrief('');
    setProjectSid(null);
  }, [stagedImages]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    addImages(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // --------------------------------------------------------
  // Save handler
  // --------------------------------------------------------
  const canSave =
    name.trim().length > 0 && stagedImages.length > 0 && !createMutation.isPending;

  const handleSave = useCallback(() => {
    if (!canSave) return;

    createMutation.mutate(
      {
        name: name.trim(),
        brief: brief.trim(),
        images: stagedImages.map((img) => img.base64),
        ...(projectSid ? { project_sid: projectSid } : {}),
      },
      {
        onSuccess: (data) => {
          const fallback = (L.style_create_success ?? '').replace('{name}', data.name);
          toast.success(data.message || fallback);
          clearForm();
        },
        onError: (err) => {
          console.error('[style_create] failed:', err);
          toast.error(`${L.style_save_failed}: ${err.message}`);
        },
      },
    );
  }, [
    canSave,
    name,
    brief,
    stagedImages,
    createMutation,
    clearForm,
    L.style_create_success,
    L.style_save_failed,
  ]);

  // --------------------------------------------------------
  // Delete handler
  // --------------------------------------------------------
  const handleDelete = useCallback(
    (intent_sid: string) => {
      setDeletingSid(intent_sid);
      deleteMutation.mutate(
        { intent_sid },
        {
          onSuccess: (data) => {
            toast.success(data.message || L.style_delete_success);
            setDeletingSid(null);
          },
          onError: (err) => {
            console.error('[style_delete] failed:', err);
            toast.error(`${L.style_delete_failed}: ${err.message}`);
            setDeletingSid(null);
          },
        },
      );
    },
    [deleteMutation, L.style_delete_success, L.style_delete_failed],
  );

  // --------------------------------------------------------
  // Render
  // --------------------------------------------------------
  // Labels not loaded yet — render nothing rather than hardcoding English
  if (!L.style_header) return null;

  return (
    <div className="flex flex-col border-t-4 border-border">
      {/* Header */}
      <div className="px-3 py-2 border-b border-border flex items-center gap-2 bg-accent/30">
        <Palette className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium">{L.style_header}</span>
      </div>

      {/* Create form */}
      <div className="px-3 py-3 border-b border-border space-y-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={L.style_name_placeholder}
          maxLength={80}
          disabled={createMutation.isPending}
          className={cn(
            'w-full px-3 py-1.5 text-sm rounded-md',
            'bg-muted border border-border',
            'focus:outline-none focus:ring-1 focus:ring-primary',
            'placeholder:text-muted-foreground',
          )}
        />

        <textarea
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          placeholder={L.style_brief_placeholder}
          rows={2}
          maxLength={500}
          disabled={createMutation.isPending}
          className={cn(
            'w-full px-3 py-1.5 text-sm rounded-md resize-none',
            'bg-muted border border-border',
            'focus:outline-none focus:ring-1 focus:ring-primary',
            'placeholder:text-muted-foreground',
          )}
        />

        {/* Project attachment — scopes alignment and savings queries */}
        <select
          value={projectSid ?? ''}
          onChange={(e) => setProjectSid(e.target.value || null)}
          disabled={createMutation.isPending}
          className={cn(
            'w-full px-3 py-1.5 text-sm rounded-md',
            'bg-muted border border-border',
            'focus:outline-none focus:ring-1 focus:ring-primary',
            !projectSid && 'text-muted-foreground',
          )}
        >
          <option value="">{L.style_project_none ?? 'Personal (no project)'}</option>
          {(projectsData?.projects ?? []).map((p: { semantic_id: string; name: string }) => (
            <option key={p.semantic_id} value={p.semantic_id}>
              {p.name}
            </option>
          ))}
        </select>

        {/* Image slots — responsive grid so slots shrink with the pane */}
        <div
          className="grid gap-2 pt-1"
          style={{ gridTemplateColumns: `repeat(${MAX_IMAGES}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: MAX_IMAGES }).map((_, i) => (
            <ImageSlot
              key={i}
              image={stagedImages[i]}
              onAdd={() => fileInputRef.current?.click()}
              onRemove={() => removeImage(stagedImages[i]?.id)}
              disabled={i > stagedImages.length}
            />
          ))}
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ImageIcon className="w-3 h-3 shrink-0" />
          <span className="truncate">
            {stagedImages.length}/{MAX_IMAGES}
          </span>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Save button */}
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className={cn(
            'w-full py-2 px-3 rounded-md text-sm font-medium',
            'flex items-center justify-center gap-2',
            'bg-primary text-primary-foreground',
            'hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors',
          )}
        >
          {createMutation.isPending || isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {isProcessing ? L.style_processing : L.style_saving}
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {L.style_save_button}
            </>
          )}
        </button>
      </div>

      {/* My Style Guides — list */}
      <div className="px-3 py-2 border-b border-border">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {L.style_my_guides}
          {items.length > 0 && <span className="ml-1 normal-case">({items.length})</span>}
        </p>
      </div>

      <div>
        {listQuery.isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-4 text-center text-xs text-muted-foreground">
            <Palette className="w-6 h-6 mx-auto mb-2 text-primary/40" />
            <p>{L.style_empty_heading}</p>
            <p className="mt-1">{L.style_empty_text}</p>
          </div>
        ) : (
          <div className="py-1">
            {items.map((item) => (
              <StyleGuideCard
                key={item.semantic_id}
                item={item}
                onDelete={handleDelete}
                isDeleting={deletingSid === item.semantic_id}
                labels={L}
              />
            ))}
          </div>
        )}
      </div>

      {listQuery.isError && (
        <div className="p-3 border-t border-border text-xs text-destructive">
          {L.style_load_failed}: {listQuery.error?.message || L.composer_unknown_error}
        </div>
      )}
    </div>
  );
}
